import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const TIER_COLOR = { cold: '#6366f1', warm: '#f59e0b', hot: '#ef4444', sales_ready: '#10b981' };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/leads/${id}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;
  if (!data) return <div style={{ padding: 40 }}>Lead not found.</div>;

  const { lead, scoreHistory, messages, sequences } = data;
  const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.email;
  const tierColor = TIER_COLOR[lead.tier] || '#6b7280';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <button onClick={() => navigate('/leads')} style={{
          background: 'none', border: '1px solid var(--border)', borderRadius: 7,
          padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--vela-text)',
        }}>← Back</button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{name}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{lead.email} · {lead.phone || 'No phone'}</p>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{
            display: 'inline-block', background: tierColor + '22', color: tierColor,
            borderRadius: 6, padding: '4px 12px', fontWeight: 700, fontSize: 13,
            textTransform: 'uppercase', marginBottom: 4,
          }}>{lead.tier?.replace('_', ' ')}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: tierColor }}>{lead.score}<span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-muted)' }}>/100</span></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Lead info */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Lead Info</h3>
          {[
            ['Company',     lead.company],
            ['Industry',    lead.industry],
            ['Province',    lead.province],
            ['City',        lead.city],
            ['Source',      lead.source],
            ['Interested in', lead.service_interest],
            ['UTM Campaign', lead.utm_campaign],
            ['Landing Page', lead.landing_page],
            ['Status',      lead.engagement_status],
            ['Consent',     new Date(lead.consent_timestamp).toLocaleDateString('en-CA')],
            ['Added',       new Date(lead.created_at).toLocaleDateString('en-CA')],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} style={{ display: 'flex', marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)', width: 130, flexShrink: 0 }}>{label}</span>
              <span style={{ fontWeight: 500 }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Sequence status */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Sequence History</h3>
          {!sequences.length && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Not enrolled in any sequence.</p>}
          {sequences.map(seq => (
            <div key={seq.id} style={{
              border: '1px solid var(--border)', borderRadius: 7, padding: '12px 16px',
              marginBottom: 10, fontSize: 13,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{seq.name}</div>
              <div style={{ color: 'var(--text-muted)' }}>
                Status: <strong>{seq.status}</strong> ·
                Step: <strong>{seq.current_step}</strong> ·
                Enrolled: {new Date(seq.enrolled_at).toLocaleDateString('en-CA')}
              </div>
            </div>
          ))}
        </div>

        {/* Score timeline */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Score History</h3>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {!scoreHistory.length && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No score events yet.</p>}
            {scoreHistory.map(e => (
              <div key={e.id} style={{
                display: 'flex', justifyContent: 'space-between',
                borderBottom: '1px solid var(--border)', padding: '8px 0', fontSize: 12,
              }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{e.event_type.replace(/_/g, ' ')}</span>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                    {new Date(e.created_at).toLocaleDateString('en-CA')}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ color: e.points >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                    {e.points >= 0 ? '+' : ''}{e.points}
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>{e.score_before} → {e.score_after}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Messages Sent</h3>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {!messages.length && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No messages sent yet.</p>}
            {messages.map(m => (
              <div key={m.id} style={{
                display: 'flex', gap: 12, borderBottom: '1px solid var(--border)',
                padding: '8px 0', fontSize: 12, alignItems: 'center',
              }}>
                <span style={{
                  background: m.channel === 'email' ? 'var(--primary-light)' : 'rgba(34,197,94,0.15)',
                  color: m.channel === 'email' ? 'var(--primary)' : 'var(--success)',
                  padding: '2px 6px', borderRadius: 4, fontWeight: 600, fontSize: 11,
                }}>{m.channel.toUpperCase()}</span>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {m.subject || m.body_preview}
                  </div>
                  <div style={{ color: 'var(--text-muted)' }}>
                    {new Date(m.sent_at).toLocaleDateString('en-CA')}
                  </div>
                </div>
                <span style={{
                  fontWeight: 600, fontSize: 11, textTransform: 'uppercase',
                  color: m.status === 'clicked' ? 'var(--primary)' :
                         m.status === 'opened'  ? 'var(--success)' :
                         m.status === 'bounced' ? 'var(--danger)' : 'var(--text-muted)',
                }}>{m.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
